import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { 
  Camera, X, Volume2, VolumeX, CheckCircle, AlertCircle, Loader2, 
  ShoppingCart, Edit, RefreshCw, ScanLine
} from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

// Barcode formats for retail products
const BARCODE_FORMATS = [
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.UPC_A,
  Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.UPC_E,
  Html5QrcodeSupportedFormats.CODE_128,
  Html5QrcodeSupportedFormats.CODE_39,
  Html5QrcodeSupportedFormats.QR_CODE,
];

// Flow states
const FLOW_STATE = {
  SCANNING: 'scanning',
  FOUND: 'found',
  NOT_FOUND: 'not_found',
  CONFIRM_ACTION: 'confirm_action',
};

const CameraBarcodeScanner = ({ 
  isOpen, 
  onClose, 
  onScan,
  products = [],
  onAddToCart,
  mode = 'cart', // 'cart' or 'edit'
  onEditProduct,
}) => {
  const [isScanning, setIsScanning] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [scannerError, setScannerError] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  
  // Single-scan flow state
  const [flowState, setFlowState] = useState(FLOW_STATE.SCANNING);
  const [foundProduct, setFoundProduct] = useState(null);
  const [scannedBarcode, setScannedBarcode] = useState(null);
  
  const html5QrCodeRef = useRef(null);
  const audioContextRef = useRef(null);
  const lastScanTimeRef = useRef(0);
  const mountedRef = useRef(true);
  
  const scannerId = 'barcode-scanner-view';

  // Play beep sound
  const playBeep = useCallback(() => {
    if (!soundEnabled) return;
    
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioContextRef.current;
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      oscillator.frequency.value = 1200;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);
      
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.15);
    } catch (e) {
      console.warn('Could not play beep:', e);
    }
  }, [soundEnabled]);

  // Pause the scanner (stop scanning but keep camera open)
  const pauseScanner = useCallback(async () => {
    if (html5QrCodeRef.current) {
      try {
        const state = html5QrCodeRef.current.getState();
        if (state === 2) {
          await html5QrCodeRef.current.pause(true);
        }
      } catch (err) {
        console.warn('[Scanner] Pause error:', err);
      }
    }
  }, []);

  // Resume the scanner
  const resumeScanner = useCallback(async () => {
    if (html5QrCodeRef.current) {
      try {
        const state = html5QrCodeRef.current.getState();
        if (state === 3) { // Paused state
          await html5QrCodeRef.current.resume();
        }
      } catch (err) {
        console.warn('[Scanner] Resume error:', err);
      }
    }
  }, []);

  // Stop the scanner completely
  const stopScanner = useCallback(async () => {
    if (html5QrCodeRef.current) {
      try {
        const state = html5QrCodeRef.current.getState();
        if (state === 2 || state === 3) {
          await html5QrCodeRef.current.stop();
        }
        html5QrCodeRef.current.clear();
      } catch (err) {
        console.warn('[Scanner] Stop error:', err);
      }
      html5QrCodeRef.current = null;
    }
    setIsScanning(false);
  }, []);

  // Handle successful scan - SINGLE SCAN MODE
  const handleScanSuccess = useCallback((decodedText) => {
    const now = Date.now();
    // Debounce - minimum 1.5s between scans
    if (now - lastScanTimeRef.current < 1500) return;
    lastScanTimeRef.current = now;
    
    playBeep();
    if (navigator.vibrate) {
      navigator.vibrate([100, 50, 100]);
    }

    // Pause scanner immediately after successful scan
    pauseScanner();
    
    // Store the barcode
    setScannedBarcode(decodedText);

    // Find the product
    const product = products.find(p => 
      p.barcode === decodedText || 
      p.barcode?.replace(/^0+/, '') === decodedText.replace(/^0+/, '')
    );

    if (product) {
      setFoundProduct(product);
      setFlowState(FLOW_STATE.FOUND);
      if (onScan) onScan(decodedText, product);
    } else {
      setFoundProduct(null);
      setFlowState(FLOW_STATE.NOT_FOUND);
      if (onScan) onScan(decodedText, null);
    }
  }, [products, onScan, playBeep, pauseScanner]);

  // Start the scanner
  const startScanner = useCallback(async () => {
    console.log('[Scanner] Starting...');
    
    const element = document.getElementById(scannerId);
    if (!element) {
      console.log('[Scanner] Element not found');
      return;
    }

    // Clean up existing instance
    if (html5QrCodeRef.current) {
      try {
        const state = html5QrCodeRef.current.getState();
        if (state === 2 || state === 3) await html5QrCodeRef.current.stop();
        html5QrCodeRef.current.clear();
      } catch (e) {
        console.log('[Scanner] Cleanup error (ignored):', e.message);
      }
      html5QrCodeRef.current = null;
    }

    setIsStarting(true);
    setScannerError(null);
    setFlowState(FLOW_STATE.SCANNING);
    setFoundProduct(null);
    setScannedBarcode(null);

    try {
      // Request camera permission
      console.log('[Scanner] Requesting camera permission...');
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ 
          video: { 
            facingMode: { ideal: 'environment' },
            width: { ideal: 1280 },
            height: { ideal: 720 }
          } 
        });
        stream.getTracks().forEach(track => track.stop());
        console.log('[Scanner] Camera permission granted');
      } catch (permErr) {
        console.log('[Scanner] Permission error:', permErr.name, permErr.message);
        if (permErr.name === 'NotAllowedError') {
          throw new Error('Camera permission denied. Please allow camera access and try again.');
        }
      }

      const html5QrCode = new Html5Qrcode(scannerId, {
        formatsToSupport: BARCODE_FORMATS,
        verbose: false,
      });
      html5QrCodeRef.current = html5QrCode;

      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      const config = {
        fps: isMobile ? 10 : 15,
        qrbox: isMobile 
          ? { width: 250, height: 140 }
          : { width: 300, height: 170 },
        aspectRatio: isMobile ? 1.777 : 1.5,
        disableFlip: false,
      };

      // Try back camera
      try {
        await html5QrCode.start(
          { facingMode: "environment" },
          config,
          handleScanSuccess,
          () => {}
        );
        console.log('[Scanner] SUCCESS: Started with back camera');
        setIsScanning(true);
        setIsStarting(false);
        return;
      } catch (backErr) {
        console.log('[Scanner] Back camera failed:', backErr.message);
      }

      // Try front camera
      try {
        await html5QrCode.start(
          { facingMode: "user" },
          config,
          handleScanSuccess,
          () => {}
        );
        console.log('[Scanner] SUCCESS: Started with front camera');
        setIsScanning(true);
        setIsStarting(false);
        return;
      } catch (frontErr) {
        console.log('[Scanner] Front camera failed:', frontErr.message);
      }

      // Try any camera
      try {
        const cameras = await Html5Qrcode.getCameras();
        if (cameras && cameras.length > 0) {
          const backCam = cameras.find(c => /back|rear|environment/i.test(c.label));
          const camToUse = backCam || cameras[0];
          
          await html5QrCode.start(
            camToUse.id,
            config,
            handleScanSuccess,
            () => {}
          );
          console.log('[Scanner] SUCCESS: Started with camera:', camToUse.label);
          setIsScanning(true);
          setIsStarting(false);
          return;
        }
      } catch (anyErr) {
        console.log('[Scanner] Camera list/start failed:', anyErr.message);
        throw anyErr;
      }

      throw new Error('No camera available');
      
    } catch (err) {
      console.error('[Scanner] Failed to start:', err);
      setIsStarting(false);
      
      let errorMsg = 'Camera error';
      if (err.name === 'NotAllowedError' || err.message?.includes('permission') || err.message?.includes('Permission')) {
        errorMsg = 'Camera permission denied. Please allow camera access in your browser settings and reload the page.';
      } else if (err.name === 'NotFoundError' || err.message?.includes('not found')) {
        errorMsg = 'No camera found on this device.';
      } else if (err.name === 'NotReadableError' || err.message?.includes('in use')) {
        errorMsg = 'Camera is being used by another app. Please close other apps and try again.';
      } else {
        errorMsg = err.message || 'Could not access camera. Please check permissions.';
      }
      
      setScannerError(errorMsg);
    }
  }, [handleScanSuccess]);

  // Handle "Yes, Add to Cart" action
  const handleAddToCart = useCallback(() => {
    if (foundProduct && onAddToCart) {
      onAddToCart(foundProduct);
      toast.success(`Added ${foundProduct.name} to cart`);
    }
    // Show "Scan another?" prompt
    setFlowState(FLOW_STATE.CONFIRM_ACTION);
  }, [foundProduct, onAddToCart]);

  // Handle "Yes, Edit" action
  const handleEditProduct = useCallback(() => {
    if (foundProduct && onEditProduct) {
      onEditProduct(foundProduct);
      // Close scanner after selecting product to edit
      handleClose();
    }
  }, [foundProduct, onEditProduct]);

  // Handle "No" - go to scan another prompt
  const handleDecline = useCallback(() => {
    setFlowState(FLOW_STATE.CONFIRM_ACTION);
  }, []);

  // Handle "Scan Another"
  const handleScanAnother = useCallback(() => {
    setFoundProduct(null);
    setScannedBarcode(null);
    setFlowState(FLOW_STATE.SCANNING);
    resumeScanner();
  }, [resumeScanner]);

  // Handle close
  const handleClose = useCallback(() => {
    stopScanner();
    setFlowState(FLOW_STATE.SCANNING);
    setFoundProduct(null);
    setScannedBarcode(null);
    setScannerError(null);
    if (onClose) onClose();
  }, [stopScanner, onClose]);

  // Start scanner when opened
  useEffect(() => {
    mountedRef.current = true;
    
    if (isOpen) {
      const timer = setTimeout(() => {
        if (mountedRef.current) {
          startScanner();
        }
      }, 300);
      return () => clearTimeout(timer);
    }
    
    return () => {
      mountedRef.current = false;
    };
  }, [isOpen, startScanner]);

  // Stop when closed
  useEffect(() => {
    if (!isOpen) {
      stopScanner();
    }
  }, [isOpen, stopScanner]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (html5QrCodeRef.current) {
        html5QrCodeRef.current.stop().catch(() => {});
      }
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {});
      }
    };
  }, []);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black" data-testid="camera-scanner">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/80 to-transparent p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-green-500 flex items-center justify-center">
              <ScanLine className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-white font-bold text-lg">
                {mode === 'cart' ? 'Scan to Cart' : 'Scan to Edit'}
              </h2>
              <p className="text-white/70 text-sm">
                {flowState === FLOW_STATE.SCANNING ? 'Point camera at barcode' : 'Product found!'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSoundEnabled(!soundEnabled)}
              className="text-white hover:bg-white/20"
            >
              {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              className="text-white hover:bg-white/20"
              data-testid="close-scanner-btn"
            >
              <X className="w-6 h-6" />
            </Button>
          </div>
        </div>
      </div>

      {/* Scanner View */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div 
          id={scannerId}
          className="w-full h-full"
          style={{ maxWidth: '100vw', maxHeight: '60vh' }}
        />
      </div>

      {/* Scan Guide Overlay - Only shown when scanning */}
      {flowState === FLOW_STATE.SCANNING && isScanning && (
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
          <div className="relative">
            <div className="w-72 h-44 border-2 border-white/50 rounded-lg">
              <div className="absolute -top-1 -left-1 w-8 h-8 border-t-4 border-l-4 border-green-400 rounded-tl-lg" />
              <div className="absolute -top-1 -right-1 w-8 h-8 border-t-4 border-r-4 border-green-400 rounded-tr-lg" />
              <div className="absolute -bottom-1 -left-1 w-8 h-8 border-b-4 border-l-4 border-green-400 rounded-bl-lg" />
              <div className="absolute -bottom-1 -right-1 w-8 h-8 border-b-4 border-r-4 border-green-400 rounded-br-lg" />
            </div>
            <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-red-500 animate-pulse" />
          </div>
          <p className="text-white/80 text-sm text-center mt-4 bg-black/50 px-4 py-2 rounded-full">
            Position barcode in frame
          </p>
        </div>
      )}

      {/* Starting Indicator */}
      {isStarting && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-white animate-spin mx-auto mb-4" />
            <p className="text-white text-lg">Starting camera...</p>
            <p className="text-white/60 text-sm mt-2">Please allow camera access if prompted</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {scannerError && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/90 p-4">
          <Card className="p-6 max-w-sm w-full">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-800 mb-2 text-center">Camera Error</h3>
            <p className="text-gray-600 text-sm mb-4 text-center whitespace-pre-line">{scannerError}</p>
            <div className="space-y-2">
              <Button onClick={startScanner} className="w-full" data-testid="retry-scanner-btn">
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
              <a 
                href="/camera-permissions-guide.html" 
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full text-center py-2 px-4 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 text-sm font-medium"
              >
                📷 View Camera Permissions Guide
              </a>
              <Button variant="outline" onClick={handleClose} className="w-full">
                Close
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Product Found - Ask to Add/Edit */}
      {flowState === FLOW_STATE.FOUND && foundProduct && (
        <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-2xl animate-slide-up">
          <div className="p-6">
            {/* Success indicator */}
            <div className="flex items-center justify-center mb-4">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-7 h-7 text-green-600" />
              </div>
            </div>
            
            {/* Product info */}
            <div className="flex items-center gap-4 mb-6 p-4 bg-gray-50 rounded-xl">
              <img 
                src={foundProduct.image} 
                alt={foundProduct.name}
                className="w-20 h-20 rounded-xl object-cover"
              />
              <div className="flex-1">
                <h3 className="font-bold text-gray-800 text-lg">{foundProduct.name}</h3>
                <p className="text-green-600 font-bold text-xl">${foundProduct.price?.toFixed(2)}</p>
                <Badge variant="outline" className="mt-1">{foundProduct.category}</Badge>
              </div>
            </div>

            {/* Action prompt */}
            <p className="text-center text-gray-700 font-medium mb-4">
              {mode === 'cart' ? 'Add this product to cart?' : 'Edit this product?'}
            </p>

            {/* Action buttons */}
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={handleDecline}
                className="flex-1 py-6"
              >
                No
              </Button>
              <Button 
                onClick={mode === 'cart' ? handleAddToCart : handleEditProduct}
                className={`flex-1 py-6 ${mode === 'cart' ? 'bg-green-500 hover:bg-green-600' : 'bg-blue-500 hover:bg-blue-600'}`}
              >
                {mode === 'cart' ? (
                  <>
                    <ShoppingCart className="w-5 h-5 mr-2" />
                    Yes, Add to Cart
                  </>
                ) : (
                  <>
                    <Edit className="w-5 h-5 mr-2" />
                    Yes, Edit
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Product Not Found */}
      {flowState === FLOW_STATE.NOT_FOUND && (
        <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-2xl animate-slide-up">
          <div className="p-6">
            {/* Warning indicator */}
            <div className="flex items-center justify-center mb-4">
              <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
                <AlertCircle className="w-7 h-7 text-yellow-600" />
              </div>
            </div>

            <h3 className="text-center font-bold text-gray-800 text-lg mb-2">Product Not Found</h3>
            <p className="text-center text-gray-500 mb-2">No product matches this barcode</p>
            <p className="text-center font-mono text-sm text-gray-400 bg-gray-100 py-2 px-4 rounded-lg mb-6">
              {scannedBarcode}
            </p>

            {/* Action buttons */}
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={handleClose}
                className="flex-1 py-6"
              >
                Exit
              </Button>
              <Button 
                onClick={handleScanAnother}
                className="flex-1 py-6 bg-blue-500 hover:bg-blue-600"
              >
                <Camera className="w-5 h-5 mr-2" />
                Scan Another
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Action - Scan Another or Exit */}
      {flowState === FLOW_STATE.CONFIRM_ACTION && (
        <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-2xl animate-slide-up">
          <div className="p-6">
            {/* Question indicator */}
            <div className="flex items-center justify-center mb-4">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Camera className="w-7 h-7 text-blue-600" />
              </div>
            </div>

            <h3 className="text-center font-bold text-gray-800 text-lg mb-2">What would you like to do?</h3>
            <p className="text-center text-gray-500 mb-6">Scan another product or exit?</p>

            {/* Action buttons */}
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={handleClose}
                className="flex-1 py-6"
              >
                <X className="w-5 h-5 mr-2" />
                Exit
              </Button>
              <Button 
                onClick={handleScanAnother}
                className="flex-1 py-6 bg-blue-500 hover:bg-blue-600"
              >
                <Camera className="w-5 h-5 mr-2" />
                Scan Another
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* CSS Animation */}
      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default CameraBarcodeScanner;
